import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { TsButtonModule, TsDividerComponent, TSIconModule, TSTreeMenuModule, TSTreeNode } from 'tsui';
import { ReportingService } from 'app/modules/reporting/reporting.service';
import { ReportTypeQueryParamValues } from '@modules/reports/reports.types';
import { map, Observable, tap } from 'rxjs';
import { DashboardService } from '@modules/dashboard/dashboard.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterModule } from '@angular/router';

@Component({
  selector: 'app-shell-report-sidebar',
  standalone: true,
  imports: [
    TSIconModule,
    TsButtonModule,
    NgTemplateOutlet,
    AsyncPipe,
    MatProgressSpinnerModule,
    TSTreeMenuModule,
    RouterModule,
    TsDividerComponent,
  ],
  templateUrl: './shell-report-sidebar.component.html',
  styleUrl: './shell-report-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellReportSidebarComponent {
  onNavigate = output<void>();
  isLoading = signal({ reportBuilder: true, pdfReports: true, dashboards: true });
  reportBuilderReports$: Observable<TSTreeNode[]>;
  pdfReports$: Observable<TSTreeNode[]>;
  dashboards$: Observable<TSTreeNode[]>;

  private readonly reportingService = inject(ReportingService);
  private readonly dashboardService = inject(DashboardService);
  private readonly router = inject(Router);

  constructor() {
    this.reportBuilderReports$ = this.reportingService
      .getReports({
        page: 1,
        per_page: 10,
        report_types: [ReportTypeQueryParamValues.SingleObject, ReportTypeQueryParamValues.TimeSeries],
      })
      .pipe(
        map((response) => {
          return (
            response.results.map((view) => {
              return {
                id: String(view.id),
                name: view.name,
                icon: 'ts_list-ul',
                link: '/reporting/builder/' + view.id,
              };
            }) ?? []
          );
        }),
        tap(() => this.isLoading.set({ ...this.isLoading(), reportBuilder: false })),
      );

    this.pdfReports$ = this.reportingService
      .getReports({
        page: 1,
        per_page: 10,
        report_types: [ReportTypeQueryParamValues.Legacy],
      })
      .pipe(
        map(
          (response) =>
            response.results.map((view) => {
              return {
                id: String(view.id),
                name: view.name,
                icon: 'ts_list-ul',
                link: '/reports/' + view.id,
              };
            }) ?? [],
        ),
        tap(() => this.isLoading.set({ ...this.isLoading(), pdfReports: false })),
      );

    this.dashboards$ = this.dashboardService.getDashboards().pipe(
      map(
        (response) =>
          response.map((view) => {
            return {
              id: String(view.id),
              name: view.name,
              icon: 'ts_list-ul',
              link: '/dashboard/' + view.id,
            };
          }) ?? [],
      ),
      tap(() => this.isLoading.set({ ...this.isLoading(), dashboards: false })),
    );
  }

  delegateLeafClickFn(route: string): void {
    this.router.navigateByUrl(route);
    this.onNavigate.emit();
  }
}
